import { gatewayClient, createService } from './connectors/httpClient';

type ParsedDirectToken = {
    exp: number;
};

const parseJWT = (token: string) => {
    if (!token) {
        return null;
    }

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''),
    );

    return JSON.parse(jsonPayload);
};

const httpClient = gatewayClient('/authentication');

let directToken = '';
let parsedDirectToken: ParsedDirectToken | null = null;
let directTokenPendingRef: Promise<string> | null = null;

function setCurrentToken(token: string) {
    directToken = token;
    parsedDirectToken = parseJWT(token);
    directTokenPendingRef = null;
}

function isDirectTokenValid() {
    if (parsedDirectToken === null) {
        return false;
    }

    const { exp } = parsedDirectToken;
    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const leftTimeToExpire = exp - currentTimeInSeconds;
    const allowedTimeUntilExpire = 60 * 20;

    return leftTimeToExpire > allowedTimeUntilExpire;
}

function rotateToken(accessToken: string) {
    return httpClient.post<{ accessToken: string }>('/rotate-token', { accessToken });
}

const authService = createService(httpClient, (client) => ({
    getDirectToken(bynderToken: string): Promise<string> {
        if (directToken && isDirectTokenValid()) {
            return Promise.resolve(directToken);
        }

        if (directTokenPendingRef) {
            return directTokenPendingRef;
        }

        directTokenPendingRef = rotateToken(bynderToken).then(({ json, status }) => {
            if (status === 200) {
                setCurrentToken(json.accessToken);

                return directToken;
            }

            setCurrentToken('');

            return '';
        });

        return directTokenPendingRef;
    },

    refreshToken() {
        return client.get<{ status: number; identityAccessToken: string }>('/refresh');
    },

    exchangeBynderToIdentityToken(data) {
        return client.post<{ loginUrl?: string; identityAccessToken: string }>(
            '/exchange-bynder-to-identity-token',
            data,
        );
    },

    registerBynderUser(data) {
        return client.post('/register-bynder-user', data);
    },

    checkToken() {
        return client.get('/check-token');
    },
}));

export default authService;
